import { useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';

type Severity = 'success' | 'info' | 'warn' | 'error';

export interface EntityToasts {
  toastRef: React.RefObject<Toast | null>;
  notifyCreated: (name: string) => void;
  notifyUpdated: (name: string) => void;
  notifyDeleted: () => void;
  notifySuccess: (message: string) => void;
  notifyError: (message: string) => void;
}

/**
 * Standardises the success/error toasts shown by the entity CRUD pages. Attach
 * `toastRef` to a `<Toast>` and call the notify helpers from dialog callbacks.
 */
export function useEntityToasts(label: string): EntityToasts {
  const toastRef = useRef<Toast>(null);

  const show = useCallback((severity: Severity, summary: string, detail: string, life = 3000) => {
    toastRef.current?.show({ severity, summary, detail, life });
  }, []);

  const notifyCreated = useCallback(
    (name: string) => show('success', `${label} Created`, `"${name}" has been created.`),
    [label, show],
  );
  const notifyUpdated = useCallback(
    (name: string) => show('success', `${label} Updated`, `"${name}" has been updated.`),
    [label, show],
  );
  const notifyDeleted = useCallback(
    () => show('success', `${label} Deleted`, `The ${label} has been deleted.`),
    [label, show],
  );
  const notifySuccess = useCallback(
    (message: string) => show('success', 'Success', message),
    [show],
  );
  const notifyError = useCallback(
    (message: string) => show('error', 'Error', message, 5000),
    [show],
  );

  return { toastRef, notifyCreated, notifyUpdated, notifyDeleted, notifySuccess, notifyError };
}
